import {useCallback, useRef, useState, type CSSProperties} from 'react';
import {ApiError, errorMessageKey, newIdempotencyKey} from '../../api';
import {ROUTES} from '../../app/routes';
import {useI18n} from '../../i18n';
import {useOrder} from '../../order';
import {useGuestSession} from '../../session';
import {
  ActionLink,
  BackLink,
  Button,
  Card,
  Screen,
  ScreenBody,
  ScreenFooter,
  Stack,
  TerminalNotice,
  TopBar,
} from '../../ui';
import {LanguageToggle} from '../Q02LanguageToggle';

/**
 * # Feedback · the post-bill rating
 *
 * Route `/feedback`. A guest who has just eaten leaves a whole 1–5 star rating
 * and, if they want to, a comment. That is the entire screen — one required
 * choice and one optional field — because a feedback form a tired diner has to
 * work at is a feedback form nobody fills in.
 *
 * ## Wired to exactly what the endpoint accepts, and no more
 *
 * `POST /v1/public/feedback` (`FeedbackSubmit`) takes three fields: `rating`
 * (required, a whole 1–5), `comment` (optional), and `order_id` (optional, the
 * bill this is about). This screen draws those three and nothing else. The
 * bible's feedback mock also draws per-dish ratings and a photo upload — **the
 * contract carries neither field, so neither is built here.** A control the API
 * cannot back is a promise the surface cannot keep (see the report).
 *
 * ## It runs inside the guest's own session
 *
 * "`public`" in the path is the route being un-gated from the paid order-pay
 * feature — it is **not** an anonymous call. The contract authenticates it with
 * the `guestSessionToken` and resolves the tenant and location from that token,
 * never the body. So this screen needs a live session exactly as Q7 does: it
 * uses the credential the table QR already issued, and when there is none it
 * points at the re-scan rather than drawing a form that cannot submit.
 *
 * `order_id` is attached when this phone knows the bill — the current order, or
 * the table's active order off the session — and simply omitted otherwise, which
 * is a rating "about the visit" rather than "about a bill". Both are valid.
 *
 * ## PDPA
 *
 * No name, phone or email is asked for or sent — a rating identifies a table,
 * not a person. The comment hint says so, so a guest does not volunteer any.
 *
 * ## Money rule
 *
 * Nothing on this screen is money, so nothing on it is ever Pandan. The thank-you
 * is the surface's ordinary Butterfly Pea confirmation ring, the same
 * `TerminalNotice tone="info"` an order-sent panel uses — a rating recorded is
 * not money moving.
 */

type SubmitState = 'idle' | 'submitting' | 'sent' | 'failed';

/** The scale the contract fixes: a whole 1–5. Not a client invention. */
const STARS = [1, 2, 3, 4, 5] as const;

/** A guest comment is free text; cap it so a paste cannot become a payload. */
const COMMENT_MAX_LENGTH = 500;

// --- style -------------------------------------------------------------------
//
// Only the geometry the shared primitives have no opinion about — the star row.
// Every colour is a theme custom property; there is no hex literal in this file.

const starRow: CSSProperties = {
  display: 'flex',
  justifyContent: 'center',
  gap: 'var(--pokpok-space-2)',
};

/** Each star is a 48px tap target with the native radio hidden inside it. */
const starLabel: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  minWidth: 48,
  minHeight: 48,
  cursor: 'pointer',
  lineHeight: 0,
};

/** A drawn star — two paths cost less than one request, and there is no asset. */
function StarMark({filled}: {filled: boolean}) {
  return (
    <svg
      width="36"
      height="36"
      viewBox="0 0 24 24"
      aria-hidden="true"
      focusable="false"
      style={{color: filled ? 'var(--color-warning)' : 'var(--color-text-secondary)'}}
    >
      <path
        d="M12 2.5l2.9 5.88 6.49.94-4.7 4.58 1.11 6.46L12 17.9l-5.8 3.05 1.1-6.46-4.69-4.58 6.49-.94L12 2.5z"
        fill={filled ? 'currentColor' : 'none'}
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinejoin="round"
      />
    </svg>
  );
}

// --- screen ------------------------------------------------------------------

export default function QrFeedback() {
  const {t} = useI18n();
  const {session, status: sessionStatus, client} = useGuestSession();
  const {order} = useOrder();

  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState('');
  const [submitState, setSubmitState] = useState<SubmitState>('idle');
  const [error, setError] = useState<ApiError | null>(null);

  /**
   * Minted once per feedback intent and reused on every retry, so a phone that
   * never saw the `201` records one rating for one meal, not two — a replay
   * returns the original entry. This is the same discipline `submitOrder` uses.
   */
  const idempotencyKey = useRef(newIdempotencyKey());

  // The bill this rating is about, when this phone knows it. Attached when
  // present, omitted otherwise — the endpoint accepts either.
  const orderId = order?.order_id ?? session?.active_order_id ?? null;

  const send = useCallback(async () => {
    if (rating < 1) return;
    setSubmitState('submitting');
    setError(null);
    const trimmed = comment.trim();
    try {
      await client.submitPublicFeedback(
        {
          rating,
          comment: trimmed === '' ? null : trimmed,
          // Only sent when known; the contract makes it optional.
          order_id: orderId,
        },
        idempotencyKey.current,
      );
      setSubmitState('sent');
    } catch (cause) {
      setError(cause instanceof ApiError ? cause : null);
      setSubmitState('failed');
    }
  }, [client, comment, orderId, rating]);

  const submitting = submitState === 'submitting';

  // --- sent: the confirmation ------------------------------------------------

  if (submitState === 'sent') {
    return (
      <Screen>
        <TopBar trailing={<LanguageToggle variant="link" />} />
        <ScreenBody center>
          <TerminalNotice tone="info" glyph="✓" title={t('feedback.sentTitle')}>
            <p className="pk-muted" style={{maxWidth: '34ch'}}>
              {t('feedback.sentBody')}
            </p>
          </TerminalNotice>
        </ScreenBody>
        <ScreenFooter bordered={false}>
          {/* The venue leads; pokpokPOS is present and subordinate — one line of
              body copy, never a lockup (D-014). Same close as the order-sent panel. */}
          <p className="pk-hint" style={{textAlign: 'center'}}>
            {t('common.attribution')}
          </p>
        </ScreenFooter>
      </Screen>
    );
  }

  // --- no usable credential --------------------------------------------------
  //
  // The table QR is the login here too: without a session there is no token to
  // authenticate the feedback, and the tenant it would be filed under is unknown.
  // Point at the re-scan rather than draw a form that cannot submit — the same
  // honest dead end Q7 draws.
  if (sessionStatus === 'idle' || sessionStatus === 'expired' || sessionStatus === 'invalid') {
    return (
      <Screen>
        <ScreenBody center>
          <TerminalNotice tone="error" glyph="!" title={t('error.session')}>
            <p className="pk-muted">{t('common.askStaff')}</p>
          </TerminalNotice>
        </ScreenBody>
        <ScreenFooter bordered={false}>
          <ActionLink to={ROUTES.invalid} variant="secondary" block>
            {t('q19.rescan')}
          </ActionLink>
          <div style={{display: 'flex', justifyContent: 'center'}}>
            <LanguageToggle variant="link" />
          </div>
        </ScreenFooter>
      </Screen>
    );
  }

  // --- the form --------------------------------------------------------------

  return (
    <Screen>
      <TopBar
        leading={<BackLink to={ROUTES.status} label={t('common.back')} />}
        trailing={<LanguageToggle variant="link" />}
      />

      <ScreenBody scroll>
        <Stack gap="wide">
          <header className="pk-stack">
            <h1 className="pk-h1">{t('feedback.title')}</h1>
            <p className="pk-muted">{t('feedback.subtitle')}</p>
          </header>

          {/* The rating. A radio group of five whole stars — the scale the
              contract fixes — announced as one group so a screen reader reads
              "Rating, 3 of 5" rather than five loose controls. */}
          <section
            role="radiogroup"
            aria-label={t('feedback.ratingLabel')}
            aria-required="true"
            className="pk-stack"
          >
            <div style={starRow}>
              {STARS.map((value) => (
                <label key={value} style={starLabel}>
                  <input
                    className="pokpok-sr-only"
                    type="radio"
                    name="feedback-rating"
                    value={value}
                    checked={rating === value}
                    onChange={() => setRating(value)}
                    aria-label={t('feedback.starLabel', {count: value})}
                  />
                  <StarMark filled={value <= rating} />
                </label>
              ))}
            </div>
          </section>

          {/* The comment. Optional, and the hint says so and says why it needs
              no name or number (PDPA). Above the field, never under it as a
              disclaimer read too late. */}
          <section className="pk-stack">
            <label className="pk-strong" htmlFor="feedback-comment">
              {t('feedback.commentLabel')}
            </label>
            <p className="pk-hint">{t('feedback.commentHint')}</p>
            <textarea
              id="feedback-comment"
              className="pk-textarea"
              value={comment}
              onChange={(event) => setComment(event.target.value)}
              maxLength={COMMENT_MAX_LENGTH}
              rows={3}
              placeholder={t('feedback.commentPlaceholder')}
              aria-label={t('feedback.commentLabel')}
            />
          </section>
        </Stack>
      </ScreenBody>

      <ScreenFooter>
        {/* Announced without moving focus off the button about to be pressed
            again — a failure here is retriable and pressing again is safe. */}
        <div role="status" aria-live="polite" className="pk-stack">
          {submitState === 'failed' ? (
            <Card tone="default">
              <div className="pk-stack">
                <strong>{t('feedback.failedTitle')}</strong>
                <span className="pk-muted">
                  {t(error ? errorMessageKey(error) : 'error.generic')}
                </span>
                <span className="pk-muted">{t('feedback.failedAction')}</span>
              </div>
            </Card>
          ) : null}
        </div>

        <Button
          variant="primary"
          onClick={() => void send()}
          // Held until a star is chosen — `rating` is the one required field, and
          // the endpoint rejects a submission without it with `400`. The reason
          // is spoken so the disabled primary is not a dead end with no
          // explanation.
          disabled={rating < 1 || submitting}
          disabledReason={rating < 1 ? t('feedback.ratingRequired') : undefined}
          aria-busy={submitting}
        >
          {submitting ? t('feedback.submitting') : t('feedback.submit')}
        </Button>
      </ScreenFooter>
    </Screen>
  );
}
